import { FC } from "react";
import { Box, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAdminLeadControllerGetAll } from "service";
import Navbar from "@/components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "@/shared/components/header-items/HeaderItems";
import HeaderStandardActions from "@/shared/components/header-items/HeaderStandardActions";
import DashboardContentLayout from "@/shared/layouts/DashboardContentLayout";
import EmptyState from "@/shared/components/EmptyState";
import MoreResultsLink from "@/shared/components/MoreResultsLink";
import { AdRoutes } from "@/constants/ad-routes";
import { useMatchingCustomersCount } from "@/hooks/use-cross-entity-matches";
import useLeadsFilters from "../hooks/useLeadsFilters";
import useLeadsCsv from "../hooks/useLeadsCsv";
import LeadsFilters from "../components/LeadsFilters";
import LeadsTable from "../components/LeadsTable";
import LeadsCsvMenu from "../components/LeadsCsvMenu";
import {
  handleLeadCheckboxChange,
  handleLeadCheckboxChangeForAll,
} from "../utils/lead-bulk-actions";

const LeadManagementPage: FC = () => {
  const navigate = useNavigate();
  const { filters, updateFilter, queryParams, onSearch, setSearchParams } =
    useLeadsFilters();

  const { data, isLoading, isError, refetch } = useAdminLeadControllerGetAll({
    queryParams,
  });

  const {
    downloadTemplate,
    downloadLeadList,
    importFile,
    isImporting,
    isLeadListLoading,
  } = useLeadsCsv(queryParams);

  // ESR-1597: surface matches on the Customers table for the same search
  // term, so a lead search doesn't silently miss someone already a customer.
  const matchingCustomersCount = useMatchingCustomersCount(filters.search);

  const hasActiveFilters = Boolean(filters.search);
  const isEmpty = !isLoading && (data?.data?.length ?? 0) === 0;
  const isTrueEmpty = isEmpty && !hasActiveFilters;

  const clearFilters = () => {
    updateFilter("search", "");
    updateFilter("page", 1);
    setSearchParams((params) => {
      params.delete("search");
      params.delete("page");
      return params;
    });
  };

  const renderContent = () => {
    if (isError) {
      return (
        <Box mt="20px">
          <Typography variant="body1" color="error">
            Failed to load leads. Check the network request to
            /api/v1/admin/leads.
          </Typography>
        </Box>
      );
    }

    if (isTrueEmpty) {
      return (
        <Box mt="20px">
          <EmptyState
            title="No Leads Yet"
            description="New prospects will appear here as they're added."
            actionLabel="Add Lead"
            actionStartIcon={<Add />}
            onAction={() => navigate(AdRoutes.leadAdd)}
          />
        </Box>
      );
    }

    if (isEmpty) {
      return (
        <Box mt="20px">
          <EmptyState
            title="No leads found"
            description="We couldn't find any leads matching your search."
            actionLabel="Clear search"
            onAction={clearFilters}
          />
        </Box>
      );
    }

    return (
      <LeadsTable
        data={data?.data}
        isLoading={isLoading}
        filters={filters}
        updateFilter={updateFilter}
        handleLeadSelection={(evt, leadId) =>
          handleLeadCheckboxChange(evt, leadId, updateFilter)
        }
        handleLeadSelectionAll={(evt) =>
          handleLeadCheckboxChangeForAll(evt, updateFilter, data)
        }
      />
    );
  };

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[{ label: "Leads", url: AdRoutes.leads }]}
          />
          <HeaderItemsRight>
            <HeaderStandardActions
              onRefresh={refetch}
              onAdd={() => navigate(AdRoutes.leadAdd)}
              addButtonText="Add Lead"
              customActions={
                <LeadsCsvMenu
                  onDownloadTemplate={downloadTemplate}
                  onImportFile={importFile}
                  onDownloadLeadList={downloadLeadList}
                  isImporting={isImporting}
                  isLeadListDisabled={isTrueEmpty || isLeadListLoading}
                />
              }
              customActionsPlacement="before"
            />
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      {!isError && !isTrueEmpty && (
        <LeadsFilters
          filters={filters}
          updateFilter={updateFilter}
          onSearch={onSearch}
          isLoading={isLoading}
          data={data}
        />
      )}
      {renderContent()}
      {!isError && (
        <MoreResultsLink
          count={matchingCustomersCount}
          entityCategoryLabel="Customers"
          targetRoute={AdRoutes.customer}
          searchTerm={filters.search}
        />
      )}
    </DashboardContentLayout>
  );
};

export default LeadManagementPage;
